/** Google Analytics 4 - loaded only after the visitor has said yes.
 *
 *  This is the opposite case to our own Reichweitenmessung in lib/track.ts:
 *  the Google tag stores cookies on the visitor's device and sends data to
 *  Google, so § 25 Abs. 1 TDDDG and Art. 6 Abs. 1 lit. a DSGVO require an
 *  informed opt-in *before* anything happens. Nothing in this file contacts
 *  googletagmanager.com until `choose('granted')` has run - the script tag is
 *  created there and nowhere else. The „Google Analytics" section of the
 *  Datenschutzerklärung (backend/seed/privacy_sections.py) describes this;
 *  when the behaviour here changes, that text has to change with it.
 *
 *  The self-hosted counting in lib/track.ts is unaffected and keeps running
 *  for everyone - it needs no consent and does not depend on this file.
 */

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { useLocation } from 'react-router-dom'

export const MEASUREMENT_ID = 'G-5R8S38NPEP'

/** Only the real site reports. Keeping localhost, the Docker stack and any
 *  preview host out means development traffic never reaches the property -
 *  and the banner stays out of the way while working on the site. */
const ANALYTICS_HOSTS = ['zvidori.de', 'www.zvidori.de']

const STORAGE_KEY = 'zvidori-analytics-consent'

export type Consent = 'granted' | 'denied'

declare global {
  interface Window {
    dataLayer: IArguments[]
    gtag?: (...args: unknown[]) => void
  }
}

/** Is there anything here to ask about? Everywhere but under Playwright and
 *  the deploy smoke test - same exclusion as lib/track.ts, so automated runs
 *  never have to dismiss a bar that appears over the footer.
 *
 *  Deliberately wider than `analyticsAvailable`: the bar shows while working
 *  on the site too, which is the only way to see it before it goes live. */
export function consentRelevant(): boolean {
  return !navigator.webdriver
}

/** May the Google tag actually load here? Only on the real site - so a choice
 *  made while developing is remembered but loads nothing, and the property
 *  never sees a page view from a laptop or the Docker stack. */
export function analyticsAvailable(): boolean {
  return consentRelevant() && ANALYTICS_HOSTS.includes(location.hostname)
}

function readStored(): Consent | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value === 'granted' || value === 'denied' ? value : null
  } catch {
    // Private mode: the visitor is asked again next time, which is the safe
    // direction - no consent is remembered, so nothing loads unasked.
    return null
  }
}

// ---------------------------------------------------------------------------
// The tag itself
// ---------------------------------------------------------------------------

let loaded = false

function loadTag(): void {
  if (loaded) return
  loaded = true

  window.dataLayer = window.dataLayer || []
  // Google's snippet verbatim: it has to be a classic function because it
  // pushes `arguments`, and gtag.js reads exactly that shape back out. A rest
  // parameter would push a plain array and the tag would ignore it.
  window.gtag = function gtag() {
    window.dataLayer.push(arguments)
  }

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`
  document.head.appendChild(script)

  window.gtag('js', new Date())
  // send_page_view: false - this is a single-page app, so the tag's automatic
  // hit would only ever record the URL the visitor happened to land on.
  // usePageView below reports every route change instead.
  window.gtag('config', MEASUREMENT_ID, { send_page_view: false })
}

/** Stop a tag that is already running and remove what it left behind. */
function revokeTag(): void {
  // Google's documented kill switch: gtag.js checks this global on every
  // single call, so it silences a script that is already in the page.
  ;(window as unknown as Record<string, boolean>)[`ga-disable-${MEASUREMENT_ID}`] = true
  window.gtag?.('consent', 'update', { analytics_storage: 'denied' })
  clearGoogleCookies()
}

function clearGoogleCookies(): void {
  const host = location.hostname
  // The tag writes on the registrable domain, so a cookie set for
  // ".zvidori.de" is not removed by expiring it host-only. Try every spelling.
  const domains = ['', host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`]

  for (const entry of document.cookie.split(';')) {
    const name = entry.split('=')[0].trim()
    if (!name.startsWith('_ga')) continue
    for (const domain of domains) {
      document.cookie =
        `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT` +
        (domain ? `; domain=${domain}` : '')
    }
  }
}

// ---------------------------------------------------------------------------
// Consent state, shared between the banner and the footer link
// ---------------------------------------------------------------------------

type State = { choice: Consent | null; asking: boolean }

let state: State = { choice: null, asking: false }
const listeners = new Set<() => void>()

function setState(next: State): void {
  state = next
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** useSyncExternalStore compares by identity, so this must be the same object
 *  until something actually changes - hence the module-level `state`. */
function getSnapshot(): State {
  return state
}

let initialised = false

function initialise(): void {
  if (initialised) return
  initialised = true
  const choice = readStored()
  setState({ choice, asking: consentRelevant() && choice === null })
}

export function useConsent(): {
  /** What the visitor decided, or null if they have not been asked yet. */
  choice: Consent | null
  /** Is the banner currently on screen? */
  asking: boolean
  /** Record a decision and act on it immediately. */
  choose: (choice: Consent) => void
  /** Put the banner back up so a decision can be changed (footer link). */
  reopen: () => void
} {
  initialise()
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const choose = useCallback((choice: Consent) => {
    try {
      localStorage.setItem(STORAGE_KEY, choice)
    } catch {
      // Unwritable storage only costs us the memory of the choice; the
      // decision still applies for the rest of this visit.
    }
    if (choice === 'granted') {
      ;(window as unknown as Record<string, boolean>)[`ga-disable-${MEASUREMENT_ID}`] = false
    } else {
      revokeTag()
    }
    setState({ choice, asking: false })
  }, [])

  const reopen = useCallback(() => {
    setState({ choice: state.choice, asking: true })
  }, [])

  return { choice: snapshot.choice, asking: snapshot.asking, choose, reopen }
}

// ---------------------------------------------------------------------------
// Page views
// ---------------------------------------------------------------------------

/** Load the tag once consent exists, then report every route change to it.
 *  Public pages only - this is called from Layout, and the admin lives
 *  outside that Layout, so Zvi's own working sessions stay out of the
 *  Google numbers exactly as they stay out of the self-hosted ones. */
export function useGoogleAnalytics(): void {
  const { pathname } = useLocation()
  const { choice } = useConsent()
  // StrictMode mounts effects twice in development; without this the first
  // page of a session would be counted two times.
  const lastSent = useRef<string | null>(null)

  useEffect(() => {
    if (choice !== 'granted' || !analyticsAvailable()) return
    loadTag()
    if (lastSent.current === pathname) return
    lastSent.current = pathname

    // The path only. Query strings on this site carry nothing worth measuring
    // and would be the one place a visitor's own words could end up in the
    // report - the self-hosted counter drops them for the same reason.
    window.gtag?.('event', 'page_view', {
      page_path: pathname,
      page_location: `${location.origin}${pathname}`,
      page_title: document.title,
    })
  }, [pathname, choice])
}
